import React, { useState, useEffect, useCallback } from 'react';
import { trackFeatureUsage } from '../../utils/analytics';
import './DonateModal.css';

const WALLETS = [
  {
    key: 'btc',
    name: 'Bitcoin',
    address: 'bc1qx3ra5rsfz4c5cnxjjnx9vnth2dp2y37s5clnuh',
    icon: 'https://cdn.tradermap.io/icons/btcusdt.png',
  },
  {
    key: 'eth',
    name: 'Ethereum',
    address: '0x922bf2335Fa382FB33BBaEd7D8edC911f23B24b8',
    icon: 'https://cdn.tradermap.io/icons/ethusdt.png',
  },
  {
    key: 'sol',
    name: 'Solana',
    address: '2k2oUjWH3XV7xzma7AMd2v8Co7MoYceGV33hb4K8NTwT',
    icon: 'https://cdn.tradermap.io/icons/solusdt.png',
  },
];

function CopyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export default function DonateModal({ open, onClose }) {
  const [walletsOpen, setWalletsOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  // Close on Escape + lock body scroll while open
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // Reset internal state when the modal is reopened
  useEffect(() => {
    if (open) {
      setWalletsOpen(false);
      setCopiedKey(null);
    }
  }, [open]);

  const copy = useCallback((wallet) => {
    const done = () => {
      setCopiedKey(wallet.key);
      trackFeatureUsage('donate_copy_address', { coin: wallet.key });
      setTimeout(() => setCopiedKey((k) => (k === wallet.key ? null : k)), 2000);
    };
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(wallet.address).then(done).catch(() => {});
      }
    } catch {
      /* clipboard unavailable — no-op */
    }
  }, []);

  if (!open) return null;

  return (
    <div className="donate-overlay" onClick={onClose} role="presentation">
      <div
        className="donate-card"
        role="dialog"
        aria-modal="true"
        aria-label="Support TraderMap"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="donate-close" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="donate-top">
          <span className="donate-logo">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <polyline points="1,12 5,7 9,9 15,3" />
              <polyline points="11,3 15,3 15,7" />
            </svg>
            tradermap.io
          </span>
          <span className="donate-badge">Ad-free</span>
        </div>

        <h2 className="donate-headline">
          10,000+ daily visitors.<br />
          <em>Revenue still zero.</em>
        </h2>
        <p className="donate-subline">This wasn&apos;t easy to write, but we wanted to be honest.</p>

        <hr className="donate-hr" />

        <p className="donate-body">
          As Tradermap grew, so did our costs. No ads, no subscriptions.
          We built this to leave behind <strong>a tool that actually works.</strong>{' '}
          But sustaining this on <strong>$0 revenue is no longer realistic.</strong>
        </p>

        <div className="donate-closing">
          If Tradermap has ever added value to your trading,
          we&apos;d <strong>truly appreciate your support</strong>{' '}
          because we <strong>need it to keep going.</strong>
        </div>

        <button
          className="donate-cta"
          onClick={() => {
            setWalletsOpen((v) => {
              const next = !v;
              if (next) trackFeatureUsage('donate_wallets_open', {});
              return next;
            });
          }}
          aria-expanded={walletsOpen}
        >
          {walletsOpen ? (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <polyline points="18 15 12 9 6 15" />
              </svg>
              Hide
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              Support us
            </>
          )}
        </button>
        <p className="donate-fine">Every contribution keeps the project alive.</p>

        {walletsOpen && (
          <div className="donate-wallets">
            <p className="donate-wallets-label">Donate with crypto</p>
            {WALLETS.map((w) => (
              <div className="donate-wallet" key={w.key}>
                <div className="donate-coin">
                  <img src={w.icon} alt={w.name} loading="lazy" />
                </div>
                <div className="donate-wallet-info">
                  <div className="donate-coin-name">{w.name}</div>
                  <div className="donate-coin-addr">{w.address}</div>
                </div>
                <button
                  className={`donate-copy ${copiedKey === w.key ? 'ok' : ''}`}
                  onClick={() => copy(w)}
                  aria-label={`Copy ${w.name} address`}
                >
                  {copiedKey === w.key ? <CheckIcon /> : <CopyIcon />}
                  {copiedKey === w.key ? 'Copied' : 'Copy'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
